/**
 * An extension of combineLatest which takes in more arguments
 */
import type { ObservableInput, Observable } from 'rxjs';
import { combineLatest } from 'rxjs';

/**
 * Like combineLatest, but takes in more than 6 inputs.
 * Just adds type information.
 */
export function combineLatestExtended<T, T2, T3, T4, T5, T6, T7>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7]>;

export function combineLatestExtended<T, T2, T3, T4, T5, T6, T7, T8>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8]>;

export function combineLatestExtended<T, T2, T3, T4, T5, T6, T7, T8, T9>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9]>;

export function combineLatestExtended<T, T2, T3, T4, T5, T6, T7, T8, T9, T10>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10]>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11]>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
    ObservableInput<T12>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12]>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
    ObservableInput<T12>,
    ObservableInput<T13>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12, T13]>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
    ObservableInput<T12>,
    ObservableInput<T13>,
    ObservableInput<T14>,
  ],
): Observable<readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12, T14]>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
    ObservableInput<T12>,
    ObservableInput<T13>,
    ObservableInput<T14>,
    ObservableInput<T15>,
  ],
): Observable<
  readonly [T, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12, T14, T15]
>;

export function combineLatestExtended<
  T,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
>(
  observables: readonly [
    ObservableInput<T>,
    ObservableInput<T2>,
    ObservableInput<T3>,
    ObservableInput<T4>,
    ObservableInput<T5>,
    ObservableInput<T6>,
    ObservableInput<T7>,
    ObservableInput<T8>,
    ObservableInput<T9>,
    ObservableInput<T10>,
    ObservableInput<T11>,
    ObservableInput<T12>,
    ObservableInput<T13>,
    ObservableInput<T14>,
    ObservableInput<T15>,
    ObservableInput<T16>,
  ],
): Observable<
  readonly [
    T,
    T2,
    T3,
    T4,
    T5,
    T6,
    T7,
    T8,
    T9,
    T10,
    T11,
    T12,
    T13,
    T14,
    T15,
    T16,
  ]
>;
export function combineLatestExtended(
  observables: readonly ObservableInput<any>[],
): Observable<any> {
  return combineLatest(
    // The typecast to non-readonly array is needed here as otherwise
    // TypeScript is using a deprecated override of combineLatest
    observables as [(typeof observables)[0]],
  ) as Observable<any>;
}
